/**
 * Player.js - Player Entity for Memex Racing Game
 * 
 * Implements free movement with "blind horse" AI system
 * Supports custom player sprites and biorhythm-based movement
 */

import Phaser from 'phaser';

export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, texture, frame, config = {}) {
    super(scene, x, y, texture, frame);
    
    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);
    
    // Player properties
    this.index = config.index || 0;
    this.name = config.name || `Player ${this.index + 1}`;
    this.isAI = config.isAI !== false; // Default to AI
    this.isCurrentPlayer = config.isCurrentPlayer || false;
    
    // Movement properties with enhanced variety - REDUCED TO 50% SPEED
    this.baseSpeed = (1.2 + Math.random() * 0.8) * 0.8; // 0.8x speed: 0.96-1.6 range (50% of original)
    this.currentSpeed = this.baseSpeed;
    this.speedMultiplier = 1;
    this.moveDirection = Math.random() * Math.PI * 2; // Random initial direction
    
    // Enhanced movement patterns
    this.movementStyle = Math.floor(Math.random() * 5); // 0-4 different movement styles
    this.turnRate = 0.1 + Math.random() * 0.4; // How quickly they turn
    this.zigzagAmplitude = 0.2 + Math.random() * 0.6; // Amplitude of zigzag movement
    this.zigzagFrequency = 0.001 + Math.random() * 0.003; // Frequency of zigzag
    this.spiralRadius = 50 + Math.random() * 100; // Radius for spiral movement
    this.spiralSpeed = 0.02 + Math.random() * 0.04; // Speed of spiral rotation
    this.driftFactor = 0.1 + Math.random() * 0.3; // How much they drift
    this.erraticness = Math.random(); // How erratic their movement is (0-1)
    
    // Burst movement properties
    this.burstChance = 0.005 + Math.random() * 0.01; // Chance to burst forward
    this.burstDuration = 0;
    this.burstSpeed = 2 + Math.random(); // 2x-3x speed during burst
    
    // Circular movement properties
    this.circleCenter = { x: 0, y: 0 };
    this.circleAngle = 0;
    this.circleRadius = 100 + Math.random() * 200;
    this.isCircling = false;
    this.circleDirection = Math.random() > 0.5 ? 1 : -1;
    
    // Momentum properties
    this.momentum = { x: 0, y: 0 };
    this.momentumDecay = 0.95 + Math.random() * 0.04;
    
    // Biorhythm for natural movement variation
    this.biorhythmSpeed = 0.5 + Math.random() * 1.5;
    this.biorhythmOffset = Math.random() * Math.PI * 2;
    this.secondaryBiorhythm = Math.random() * Math.PI * 2;
    this.secondaryBioSpeed = 0.3 + Math.random() * 0.7;
    
    // Status effects
    this.paralyzed = false;
    this.hasBubble = false;
    this.magnetized = false;
    this.skills = [];
    
    // Stuck detection
    this.stuckCounter = 0;
    this.lastPositions = [];
    this.maxStuckFrames = 120;
    
    // Physics setup - REDUCED TO 50% SPEED
    this.body.setSize(42, 42); // Collision box for 48x48 sprite
    this.body.setOffset(3, 3); // Center the collision box
    this.body.setMaxVelocity(300, 300); // 50% max velocity (reduced from 600)
    this.body.setDrag(100);
    
    // Display setup - Fixed 48x48 size
    this.setOrigin(0.5, 0.5);
    this.setDisplaySize(48, 48); // Fixed 48x48 pixel display
    this.setDepth(100 + this.index); // Layer players properly
    
    // Create name text
    this.createNameText();
    
    // Custom texture support
    this.customTexture = config.customTexture || null;
    if (this.customTexture) {
      this.setTexture(this.customTexture);
    }
    
    // Active state
    this.active = true;
    this.visible = true;
  }
  
  createNameText() {
    const fontSize = this.isCurrentPlayer ? '14px' : '12px';
    const color = this.isCurrentPlayer ? '#ffff00' : '#ffffff';
    const strokeThickness = this.isCurrentPlayer ? 3 : 2;
    
    this.nameText = this.scene.add.text(this.x, this.y + 20, this.name, {
      fontSize: fontSize,
      fontFamily: 'Courier New',
      color: color,
      stroke: '#000000',
      strokeThickness: strokeThickness
    }).setOrigin(0.5);
    
    this.nameText.setDepth(this.depth + 1);
  }
  
  /**
   * Update movement using "blind horse" AI
   * Based on claudeweb implementation
   */
  updateMovement(delta) {
    // Skip if paralyzed or inactive
    if (this.paralyzed || !this.active) {
      if (this.body) {
        this.body.setVelocity(0, 0);
      }
      return;
    }
    
    const time = this.scene.time.now;
    
    // AGGRESSIVE STUCK DETECTION - Check immediately
    if (this.stuckCounter > 5) {
      console.log(`[Player ${this.index}] Stuck for ${this.stuckCounter} frames - Quick teleporting!`);
      this.quickTeleport();
      return; // Skip this frame after teleport
    }
    
    // Enhanced movement variety based on movement style
    this.updateMovementPattern(time, delta);
    
    // Calculate dual biorhythm effects for more complex movement
    const biorhythm1 = Math.sin(time * 0.0003 * this.biorhythmSpeed + this.biorhythmOffset);
    const biorhythm2 = Math.cos(time * 0.0002 * this.secondaryBioSpeed + this.secondaryBiorhythm);
    const combinedBiorhythm = (biorhythm1 * 0.7 + biorhythm2 * 0.3);
    
    // Speed variation with burst mechanics
    if (this.burstDuration > 0) {
      this.currentSpeed = this.baseSpeed * this.speedMultiplier * this.burstSpeed;
      this.burstDuration -= delta;
    } else {
      this.currentSpeed = this.baseSpeed * this.speedMultiplier * (1 + combinedBiorhythm * 0.25);
      // Random burst activation
      if (Math.random() < this.burstChance) {
        this.burstDuration = 500 + Math.random() * 1000; // 0.5-1.5 seconds
        console.log(`[Player ${this.index}] Speed burst activated!`);
      }
    }
    
    // Calculate movement
    const moveSpeed = this.currentSpeed * delta / 16;
    const velocityX = Math.cos(this.moveDirection) * moveSpeed * 60; // Scale for physics
    const velocityY = Math.sin(this.moveDirection) * moveSpeed * 60;
    
    // SIMPLIFIED: Just check boundaries, allow free movement within safe area
    const nextX = this.x + velocityX * delta / 1000;
    const nextY = this.y + velocityY * delta / 1000;
    
    // Check if we're hitting the screen boundaries (full screen)
    const margin = 30; // Smaller buffer from edge
    const screenWidth = this.scene.trackWidth || 1920;  // Full width
    const screenHeight = this.scene.trackHeight || 1080; // Full height
    const hitLeft = nextX < margin;
    const hitRight = nextX > (screenWidth - margin);
    const hitTop = nextY < margin;
    const hitBottom = nextY > (screenHeight - margin);
    
    // Bounce off boundaries
    if (hitLeft || hitRight) {
      this.moveDirection = Math.PI - this.moveDirection; // Reverse X direction
      if (this.body) {
        this.body.setVelocity(0, 0); // Stop to prevent getting stuck
      }
    }
    if (hitTop || hitBottom) {
      this.moveDirection = -this.moveDirection; // Reverse Y direction
      if (this.body) {
        this.body.setVelocity(0, 0); // Stop to prevent getting stuck
      }
    }
    
    // Apply velocity only if position is valid
    if (this.body) {
      this.body.setVelocity(velocityX, velocityY);
    }
    
    // REMOVED: No more track validation - allow completely free movement within bounds
    
    // Additional corner detection - check if player is outside safe area
    const safeMargin = this.scene.trackWidth * 0.1; // 10% margin
    const nearLeftEdge = this.x < safeMargin;
    const nearRightEdge = this.x > this.scene.trackWidth - safeMargin;
    const nearTopEdge = this.y < safeMargin;
    const nearBottomEdge = this.y > this.scene.trackHeight - safeMargin;
    
    // If stuck in a corner (near two edges), force teleport immediately
    if ((nearLeftEdge || nearRightEdge) && (nearTopEdge || nearBottomEdge)) {
      // NO DELAY - Teleport immediately if in corner
      console.log(`[Player] Corner trap detected for player ${this.index}, immediate teleport!`);
      this.quickTeleport();
      this.stuckCounter = 0;
    }
    
    // Add player repulsion to prevent clustering
    this.handlePlayerRepulsion();
    
    // Update position tracking for stuck detection
    this.updatePositionTracking(time);
    
    // Update name text position
    if (this.nameText) {
      this.nameText.x = this.x;
      this.nameText.y = this.y + 20;
    }
  }
  
  /**
   * Handle player repulsion to prevent clustering
   */
  handlePlayerRepulsion() {
    if (!this.scene.players || !this.active) return;
    
    const repulsionRadius = 60; // Reduced radius to prevent excessive spreading
    const repulsionStrength = 0.2; // Reduced strength to be more subtle
    
    for (const otherPlayer of this.scene.players) {
      if (otherPlayer === this || !otherPlayer.active) continue;
      
      const distance = Phaser.Math.Distance.Between(this.x, this.y, otherPlayer.x, otherPlayer.y);
      
      if (distance < repulsionRadius && distance > 0) {
        // Calculate repulsion direction (away from other player)
        const repulsionAngle = Phaser.Math.Angle.Between(otherPlayer.x, otherPlayer.y, this.x, this.y);
        
        // Apply subtle repulsion to movement direction (no track constraints)
        const currentDir = this.moveDirection;
        const angleDiff = Phaser.Math.Angle.ShortestBetween(currentDir, repulsionAngle);
        
        // Gradually adjust direction away from other player
        this.moveDirection += angleDiff * repulsionStrength * (repulsionRadius - distance) / repulsionRadius;
      }
    }
  }
  
  /**
   * Track positions for stuck detection
   */
  updatePositionTracking(time) {
    this.lastPositions.push({ x: this.x, y: this.y, time: time });
    
    // Keep only last 5 positions for faster detection
    if (this.lastPositions.length > 5) {
      this.lastPositions.shift();
    }
    
    // AGGRESSIVE: Check if stuck (moved less than 2 pixels in just 5 frames)
    if (this.lastPositions.length >= 5) {
      const oldPos = this.lastPositions[0];
      const distance = Phaser.Math.Distance.Between(
        this.x, this.y,
        oldPos.x, oldPos.y
      );
      
      if (distance < 2) {
        this.stuckCounter += 2; // Increment faster
        // Also reset velocity immediately when stuck detected
        if (this.body) {
          this.body.setVelocity(0, 0);
        }
      } else if (this.stuckCounter > 0) {
        this.stuckCounter = Math.max(0, this.stuckCounter - 1); // Decrement slower
      }
    }
  }
  
  // REMOVED: No more track direction adjustment - free movement only
  
  /**
   * Quick teleport - SIMPLIFIED: Just move to random position in safe area
   */
  quickTeleport() {
    // Clear velocity first
    if (this.body) {
      this.body.setVelocity(0, 0);
    }
    
    // Get safe bounds (full screen)
    const screenWidth = this.scene.trackWidth || 1920;
    const screenHeight = this.scene.trackHeight || 1080;
    const minX = 100; // Smaller buffer from left edge
    const maxX = screenWidth - 100; // Smaller buffer from right edge
    const minY = 100; // Smaller buffer from top edge  
    const maxY = screenHeight - 100; // Smaller buffer from bottom edge
    
    // Teleport to random position within safe bounds
    this.x = minX + Math.random() * (maxX - minX);
    this.y = minY + Math.random() * (maxY - minY);
    
    // Reset stuck counters
    this.stuckCounter = 0;
    this.offTrackCounter = 0;
    this.lastPositions = [];
    
    // Random new direction
    this.moveDirection = Math.random() * Math.PI * 2;
    
    // Visual feedback - quick green flash
    this.setTint(0x00ff00);
    this.scene.time.delayedCall(200, () => {
      this.clearTint();
    });
    
    console.log(`[Player ${this.index}] Teleported to (${Math.round(this.x)}, ${Math.round(this.y)})`);
    
    // Clear velocity again after teleport
    if (this.body) {
      this.body.setVelocity(0, 0);
    }
  }
  
  /**
   * Update movement pattern based on style
   */
  updateMovementPattern(time, delta) {
    const changeChance = 0.01 + this.erraticness * 0.03; // More erratic = more changes
    
    switch(this.movementStyle) {
      case 0: // Zigzag movement
        const zigzag = Math.sin(time * this.zigzagFrequency) * this.zigzagAmplitude;
        this.moveDirection += zigzag * delta * 0.001;
        if (Math.random() < changeChance) {
          this.moveDirection += (Math.random() - 0.5) * Math.PI * 0.5;
        }
        break;
        
      case 1: // Spiral movement
        this.spiralRadius *= 0.998; // Slowly decrease spiral
        if (this.spiralRadius < 30) this.spiralRadius = 150; // Reset spiral
        const spiralOffset = Math.sin(time * this.spiralSpeed) * 0.02;
        this.moveDirection += spiralOffset + (Math.random() - 0.5) * 0.01;
        if (Math.random() < changeChance * 2) {
          this.moveDirection += (Math.random() - 0.5) * Math.PI * 0.8;
        }
        break;
        
      case 2: // Drift movement
        this.moveDirection += (Math.random() - 0.5) * this.driftFactor * 0.1;
        // Occasional sharp turns
        if (Math.random() < changeChance) {
          this.moveDirection += (Math.random() - 0.5) * Math.PI * 0.6;
        }
        // Add momentum effect
        this.momentum.x *= this.momentumDecay;
        this.momentum.y *= this.momentumDecay;
        break;
        
      case 3: // Circular/Arc movement
        if (!this.isCircling && Math.random() < 0.01) {
          // Start circling
          this.isCircling = true;
          this.circleCenter = { x: this.x, y: this.y };
          this.circleAngle = 0;
          this.circleDirection = Math.random() > 0.5 ? 1 : -1;
        }
        
        if (this.isCircling) {
          this.circleAngle += 0.05 * this.circleDirection;
          const targetX = this.circleCenter.x + Math.cos(this.circleAngle) * this.circleRadius;
          const targetY = this.circleCenter.y + Math.sin(this.circleAngle) * this.circleRadius;
          this.moveDirection = Math.atan2(targetY - this.y, targetX - this.x);
          
          // Stop circling after a while
          if (Math.random() < 0.005) {
            this.isCircling = false;
            this.moveDirection += Math.random() * Math.PI * 2;
          }
        } else {
          // Normal movement with occasional direction changes
          if (Math.random() < changeChance * 1.5) {
            this.moveDirection += (Math.random() - 0.5) * Math.PI * this.turnRate;
          }
        }
        break;
        
      case 4: // Erratic/Chaotic movement
        // Frequent random direction changes
        if (Math.random() < changeChance * 3) {
          const changeAmount = (Math.random() - 0.5) * Math.PI * (0.3 + this.erraticness * 0.7);
          this.moveDirection += changeAmount;
        }
        // Occasional complete direction reversal
        if (Math.random() < 0.002) {
          this.moveDirection += Math.PI + (Math.random() - 0.5) * 0.5;
          console.log(`[Player ${this.index}] Sudden reversal!`);
        }
        // Random speed variations
        if (Math.random() < 0.01) {
          this.speedMultiplier = 0.5 + Math.random() * 1.5;
        }
        break;
        
      default:
        // Fallback to simple random movement
        if (Math.random() < changeChance) {
          this.moveDirection += (Math.random() - 0.5) * Math.PI * 0.3;
        }
    }
    
    // Occasional style switching for extra variety (1% chance)
    if (Math.random() < 0.001) {
      const oldStyle = this.movementStyle;
      this.movementStyle = Math.floor(Math.random() * 5);
      console.log(`[Player ${this.index}] Switched movement style from ${oldStyle} to ${this.movementStyle}`);
    }
    
    // Add some universal random wobble to all styles
    const wobble = Math.sin(time * 0.002) * 0.01 * (1 + this.erraticness);
    this.moveDirection += wobble;
  }
  
  // REMOVED: No more track-based emergency teleport - using simplified quickTeleport instead
  
  // REMOVED: No more track-based wall collision handling - simple boundary bouncing only
  
  /**
   * Reset player for new race
   */
  reset(x, y) {
    this.x = x;
    this.y = y;
    this.currentSpeed = this.baseSpeed;
    this.speedMultiplier = 1;
    this.paralyzed = false;
    this.hasBubble = false;
    this.magnetized = false;
    this.skills = [];
    this.clearTint();
    this.body.setVelocity(0, 0);
    this.moveDirection = Math.random() * Math.PI * 2;
    this.active = true;
    this.lastPositions = [];
    this.stuckCounter = 0;
    
    // Reset name text position
    if (this.nameText) {
      this.nameText.x = this.x;
      this.nameText.y = this.y + 20;
    }
  }
  
  /**
   * Apply custom texture from uploaded image
   */
  applyCustomTexture(textureKey) {
    if (this.scene.textures.exists(textureKey)) {
      this.setTexture(textureKey);
      this.customTexture = textureKey;
      this.setDisplaySize(48, 48); // Maintain 48x48 size
    }
  }
  
  /**
   * Apply speed boost
   */
  applySpeedBoost(multiplier, duration) {
    this.speedMultiplier = multiplier;
    
    // Visual effect
    this.scene.tweens.add({
      targets: this,
      scaleX: this.scaleX * 1.2,
      scaleY: this.scaleY * 1.2,
      duration: 200,
      yoyo: true
    });
    
    // Reset after duration
    this.scene.time.delayedCall(duration, () => {
      this.speedMultiplier = 1;
    });
  }
  
  /**
   * Clean up when destroyed
   */
  destroy() {
    if (this.nameText) {
      this.nameText.destroy();
      this.nameText = null;
    }
    super.destroy();
  }
}